import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import * as Dialog from '@radix-ui/react-dialog';

import { cn } from '@/styles/tailwind-utils';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
}

export function Modal({ isOpen, onClose, title, children, footer }: ModalProps) {
  const { t } = useTranslation();
  const titleId = useId();

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            'fixed inset-0 z-50 bg-black/50',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          )}
        />
        <Dialog.Content
          aria-modal
          aria-labelledby={titleId}
          className={cn(
            'fixed left-1/2 top-1/2 z-50 w-full max-w-lg -translate-x-1/2 -translate-y-1/2',
            'rounded-md bg-surface p-6 shadow-lg',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
          )}
        >
          <Dialog.Title id={titleId} className="text-lg font-semibold text-foreground">
            {title}
          </Dialog.Title>
          <div className="mt-4 text-sm text-muted-foreground">{children}</div>
          {footer && <div className="mt-6 flex justify-end gap-3">{footer}</div>}
          <Dialog.Close asChild>
            <button
              aria-label={t('layout.closeDialog')}
              className="absolute right-4 top-4 rounded-xs p-1 text-muted-foreground hover:text-foreground focus:outline-none focus:ring-2 focus:ring-brand"
            >
              ✕
            </button>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
